import React, { memo, useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardHeader } from "reactstrap";
import "./AccountsInfoCards.css";
import "./AccountsInfoCardsTheme2.css";
import { formatDateForDisplay } from "utils/dateUtils";
import { formatPriceWithDollar } from "utils/currency";
import { getRemainingLateFeeDue, getScheduleTotalDue } from "utils/paymentSchedule";
import { formatAccountTypeLabel } from "utils/accountTypeLabels";
import {
  formatAccountStatusLabel,
  getAccountStatusColor,
} from "utils/accountStatusColors";
import { useNotification } from "contexts/NotificationContext";
import { resolveInsuranceDisplayStatus } from "utils/insuranceStatus";

const NEUTRAL_ACCENT_BORDER = "rgb(194, 218, 231)";
const SURFACE_LIGHT = "#ffffff";
const SURFACE_DARK = "#272A3D";

function getTheme2CardSurfaceStyle(isLightTheme, accentBorderColor) {
  return {
    width: "100%",
    background: isLightTheme ? SURFACE_LIGHT : SURFACE_DARK,
    "--acc2-accent-border": accentBorderColor,
    boxShadow: "none",
  };
}

function getLegacyCardStyle(accentBorderColor) {
  return {
    width: "100%",
    borderLeft: `8px solid ${accentBorderColor}`,
  };
}

const isUnpaidSchedulePayment = (payment) => payment?.status !== "paid";

const getSchedulePaymentType = (payment) => payment?.type || payment?.payment_type;

const isDefDownSchedulePayment = (payment) => getSchedulePaymentType(payment) === "def_down";

/** IDMS: unpaid down payments count as due on/after their due date even when day_past_due is still 0. */
const isActualDueSchedulePayment = (payment) => {
  if (!isUnpaidSchedulePayment(payment)) return false;
  if ((payment?.day_past_due || 0) > 0) return true;
  if (!isDefDownSchedulePayment(payment) || !payment?.date) return false;

  const due = new Date(payment.date);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due.getTime() <= today.getTime();
};

function getAccountStatusAccentColor(actStatus, flags, isBankruptcy) {
  return getAccountStatusColor(actStatus, { flags, isBankruptcy });
}

const AccountsInfoCards = memo(
  ({
    data,
    getActiveColorStyle,
    isLightTheme = false,
    isTheme2 = false,
  }) => {
    const { t } = useTranslation();
    const [showAccountDetails, setShowAccountDetails] = useState(false);
    const [showApplicantDetails, setShowApplicantDetails] = useState(false);
    const [showDealDetails, setShowDealDetails] = useState(false);
    const [showInsuranceDetails, setShowInsuranceDetails] = useState(false);
    const [showLoanDetails, setShowLoanDetails] = useState(false);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);

    const isTheme2MobileExpanded = isTheme2 && isMobile;
    const { showNotification } = useNotification();

    const [currentTheme, setCurrentTheme] = useState(() => {
      const darkMode = localStorage.getItem("darkMode");
      return darkMode ? JSON.parse(darkMode) : true;
    });

    const getTextColor = () => {
      if (isTheme2) {
        return isLightTheme ? "#3c4858" : "#ffffff";
      }
      return currentTheme ? "white" : "black";
    };

    const getCardStyle = (accentBorderColor) =>
      isTheme2
        ? getTheme2CardSurfaceStyle(isLightTheme, accentBorderColor)
        : getLegacyCardStyle(accentBorderColor);

    const cardClassName = isTheme2 ? "acc2-card" : undefined;
    const cardBodyClassName = isTheme2 ? "acc2-card-body" : undefined;

    const promiseAccentColor = useMemo(() => {
      if (!data?.promise_payment || data?.promise_payment?.total === 0) {
        return NEUTRAL_ACCENT_BORDER;
      }
      if (data?.promise_payment?.last_promise_status === "pending") return "#f6ad55";
      if (data?.promise_payment?.last_promise_status === "kept") return "#28a745";
      return "#ff5c5c";
    }, [data?.promise_payment]);

    const latestInsurance = data?.insurance_history?.[0];
    const insuranceDisplay = useMemo(
      () => resolveInsuranceDisplayStatus(latestInsurance),
      [latestInsurance]
    );
    const insuranceAccentColor = insuranceDisplay.color === "inherit"
      ? NEUTRAL_ACCENT_BORDER
      : insuranceDisplay.color;

    const getStateLabel = (state) => {
      if (!state) {
        return "-";
      }
      if (typeof state === "object") {
        return state.abbreviation || state.name || "-";
      }
      return state;
    };

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth < 992);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
      const handleStorageChange = () => {
        const darkMode = localStorage.getItem("darkMode");
        const newTheme = darkMode ? JSON.parse(darkMode) : true;
        setCurrentTheme(newTheme);
      };

      window.addEventListener("storage", handleStorageChange);

      const interval = setInterval(() => {
        const darkMode = localStorage.getItem("darkMode");
        const newTheme = darkMode ? JSON.parse(darkMode) : true;
        if (newTheme !== currentTheme) {
          setCurrentTheme(newTheme);
        }
      }, 100);

      return () => {
        window.removeEventListener("storage", handleStorageChange);
        clearInterval(interval);
      };
    }, [currentTheme]);

    /** `balances` overlay must not overwrite `current_bal` with `undefined` (same rule as BalancesTab). */
    const mergedAccountBalances = useMemo(() => {
      const base = data?.current_bal && typeof data.current_bal === "object" ? data.current_bal : {};
      const overlay = data?.balances && typeof data.balances === "object" ? data.balances : {};
      const out = { ...base };
      Object.keys(overlay).forEach((key) => {
        const v = overlay[key];
        if (v !== undefined) out[key] = v;
      });
      return out;
    }, [data?.current_bal, data?.balances]);


    const pastDueStats = useMemo(() => {
      const payments = data?.remaining_payments || [];
      const pastDueItems = payments.filter(isActualDueSchedulePayment);

      const actualPastDueAmount = pastDueItems.reduce(
        (sum, p) => sum + getScheduleTotalDue(p),
        0
      );
      const actualPastDueCount = pastDueItems.length;

      const filteredItems = payments.filter(
        (p) => getSchedulePaymentType(p) === "regular_payment" && (p.day_past_due || 0) > 0
      );
      const filteredPastDueAmount = filteredItems.reduce(
        (sum, p) => sum + getScheduleTotalDue(p),
        0
      );
      const filteredPastDueCount = filteredItems.length;

      // Find the oldest past due payment for the date/days display
      let oldestDateText = "";
      let oldestDayPastDue = 0;
      if (pastDueItems.length > 0) {
        const oldest = [...pastDueItems].sort((a, b) => new Date(a.date) - new Date(b.date))[0];
        const d = new Date(oldest.date);
        const dateStr = `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
        oldestDayPastDue = Number(oldest.day_past_due) || 0;
        oldestDateText = ` - ${dateStr} - Days ${oldestDayPastDue}`;
      }

      const getPastDueColor = (lateDay) => {
        let rowBgColor = '#28a745';
        if (lateDay > 0) rowBgColor = '#f6ad55';
        if (lateDay > 15) rowBgColor = '#f6a04d';
        if (lateDay > 30) rowBgColor = '#d97706';
        if (lateDay > 60) rowBgColor = '#bf6d00';
        if (lateDay >= 90) rowBgColor = '#dc3545';
        if (lateDay >= 120) rowBgColor = '#dc3545';
        return rowBgColor;
      };

      const getTierInfo = (lateDay) => {
        if (lateDay === 0) return { name: 'T0', label: t('accountsInfoCards.tiers.current', 'Current') };
        if (lateDay <= 15) return { name: 'T1', label: t('accountsInfoCards.tiers.late0_15', '0-15 Late') };
        if (lateDay <= 30) return { name: 'T2', label: t('accountsInfoCards.tiers.late16_30', '16-30 Late') };
        if (lateDay <= 60) return { name: 'T3', label: t('accountsInfoCards.tiers.late31_60', '31-60 Late') };
        if (lateDay <= 90) return { name: 'T4', label: t('accountsInfoCards.tiers.late61_90', '61-90 Late') };
        if (lateDay < 120) return { name: 'T5', label: t('accountsInfoCards.tiers.late91_120', '91-120 Late') };
        return { name: 'T6', label: t('accountsInfoCards.tiers.late120Plus', '120+ Late') };
      };

      const tier = getTierInfo(oldestDayPastDue);

      return {
        actualAmount: formatPriceWithDollar(actualPastDueAmount),
        actualCount: actualPastDueCount,
        filteredAmount: formatPriceWithDollar(filteredPastDueAmount),
        filteredCount: filteredPastDueCount,
        oldestDateText,
        pastDueColor: getPastDueColor(oldestDayPastDue),
        tierText: `${tier.name} - ${tier.label}`
      };
    }, [data, t]);

    // iDMS "Customer Credit" = available customer credit balance (not remaining schedule sum).
    // DealIQ stores it on account_balance_summary.credit_limit / customer_credit when present.
    const customerCreditText = useMemo(() => {
      const summary = data?.account_balance_summary || {};
      const raw =
        data?.customer_credit ??
        summary.customer_credit ??
        summary.credit_limit ??
        data?.current_bal?.customer_credit ??
        0;
      const n = Number(raw);
      return formatPriceWithDollar(Number.isFinite(n) ? n : 0);
    }, [data]);

    const { lastPaidDateText, lastPaidAmountText } = useMemo(() => {
      const lastPayment = data?.payments?.[0];
      if (!lastPayment) return { lastPaidDateText: "-", lastPaidAmountText: "($0.00)" };

      const d = new Date(lastPayment.date);
      const dateText = d.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });

      return {
        lastPaidDateText: dateText,
        lastPaidAmountText: `(${formatPriceWithDollar(lastPayment.amount || 0)})`
      };
    }, [data]);

    const capitalizeWords = (str) => {
      if (!str) return "-";
      return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const actStatusText = formatAccountStatusLabel(data?.act_status, {
      isLock: Boolean(data?.is_lock),
      flags: data?.flags,
      isBankruptcy: Boolean(data?.is_bankruptcy),
    });

    const accountTypeText = formatAccountTypeLabel(data?.account_type);

    const nextDueText = useMemo(() => {
      const firstRegular = data?.remaining_payments?.find(
        (p) => getSchedulePaymentType(p) === "regular_payment"
      );
      if (!firstRegular) return "-";
      const amount = formatPriceWithDollar(firstRegular.amount);
      const d = new Date(firstRegular.date);
      const dateStr = `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffMs = d.getTime() - today.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      return `${amount} - ${dateStr} - Days ${diffDays}`;
    }, [data?.remaining_payments]);

    const downPmtDueTotal = useMemo(() => {
      const payments = data?.remaining_payments || [];
      const scheduleDue = payments
        .filter((p) => isDefDownSchedulePayment(p) && isActualDueSchedulePayment(p))
        .reduce((sum, p) => sum + (p.amount || 0), 0);

      if (scheduleDue > 0) {
        return formatPriceWithDollar(scheduleDue);
      }

      const balanceDue = Number(mergedAccountBalances?.current_def_down) || 0;
      return formatPriceWithDollar(balanceDue);
    }, [data?.remaining_payments, mergedAccountBalances?.current_def_down]);

    return (
      <div
        className={classNames({
          "accounts-info-cards-theme2": isTheme2,
          "accounts-info-cards-theme2--light": isTheme2 && isLightTheme,
          "accounts-info-cards-theme2--dark": isTheme2 && !isLightTheme,
        })}
      >
        {data?.act_status === "out_for_repo" && (
          <div
            className="d-flex justify-content-center align-items-center mb-2 py-2 accounts-out-for-repo-banner"
            style={{ backgroundColor: "#ffe600", color: "black", marginTop: -5 }}
          >
            <span style={{ fontSize: 15, fontWeight: 600, color: "black" }}>
              {t('accountsInfoCards.outForRepo')}
            </span>
          </div>
        )}
        {data?.alert_message && (
          <div
            className="d-flex justify-content-center align-items-center mb-2"
            style={{
              backgroundColor: data.alert_background_color || "#EB0C0C",
              color: data.alert_font_color || "#ffffff",
              height: "40px",
              width: "100%"
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600 }}>
              {data.alert_message}
            </span>
          </div>
        )}
        <div className="d-flex flex-column flex-lg-row" style={{ gap: 12 }}>
          <Card
            className={cardClassName}
            style={getCardStyle(NEUTRAL_ACCENT_BORDER)}
          >
            <CardHeader className="pt-1">
              <h4 className="mb-0">
                {data?.account_type === "wholesale"
                  ? t('accountsInfoCards.wholesalerDetail')
                  : t('accountsInfoCards.borrowerInfo')}
              </h4>
            </CardHeader>
            <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
              <div>
                {data?.account_type === "wholesale" ? (
                  <>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.wholesaler')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.wholesaler?.name || data?.wholesaler?.legal_name || "-"}
                      </span>
                    </div>

                    {data?.wholesaler?.phone_1 && (
                      <div className="aic-row">
                        <span className="aic-label">{t('accountsInfoCards.homePhone')}:</span>
                        <span
                          className="aic-value"
                          style={{ color: getTextColor() }}
                        >
                          {data?.wholesaler?.phone_1 || "-"}
                        </span>
                      </div>
                    )}
                    {data?.wholesaler?.phone_2 && (
                      <div className="aic-row">
                        <span className="aic-label">{t('accountsInfoCards.cellPhone')}:</span>
                        <span className="aic-value" style={{ color: getTextColor() }}>
                          {data?.wholesaler?.phone_2 || "-"}
                        </span>
                      </div>
                    )}
                    {(isTheme2MobileExpanded || showApplicantDetails) && (
                      <div className="aic-row">
                        <span className="aic-label">{t('accountsInfoCards.email')}:</span>
                        <span
                          className="aic-value"
                          style={{ color: getTextColor() }}
                        >
                          {data?.wholesaler?.email || "-"}
                        </span>
                      </div>
                    )}
                    {data?.wholesaler?.email && !isTheme2MobileExpanded && (
                      <div className="mb-1">
                        <a
                          href="#applicant-details"
                          className="aic-link"
                          style={{ color: getActiveColorStyle().borderColor }}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowApplicantDetails(!showApplicantDetails);
                          }}
                        >
                          {showApplicantDetails
                            ? t("dealInfoCards.lessDetails")
                            : t("dealInfoCards.moreDetails")}
                        </a>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.borrower')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.customer?.full_name || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.customerCredit')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {customerCreditText}
                      </span>
                    </div>
                    {data?.customer?.home_phone && (
                      <div className="aic-row">
                        <span className="aic-label">{t('accountsInfoCards.homePhone')}:</span>
                        <span
                          className="aic-value"
                          style={{ color: getTextColor() }}
                        >
                          {data?.customer?.home_phone || "-"}
                        </span>
                      </div>
                    )}
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.cellPhone')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.customer?.cell_phone || "-"}
                      </span>
                    </div>
                    {(isTheme2MobileExpanded || showApplicantDetails) && (
                      <div className="aic-row">
                        <span className="aic-label">{t('accountsInfoCards.email')}:</span>
                        <span
                          className="aic-value"
                          style={{ color: getTextColor() }}
                        >
                          {data?.customer?.email || "-"}
                        </span>
                      </div>
                    )}
                    {data?.customer?.email && !isTheme2MobileExpanded && (
                      <div className="mb-1">
                        <a
                          href="#applicant-details"
                          className="aic-link"
                          style={{ color: getActiveColorStyle().borderColor }}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowApplicantDetails(!showApplicantDetails);
                          }}
                        >
                          {showApplicantDetails
                            ? t("dealInfoCards.lessDetails")
                            : t("dealInfoCards.moreDetails")}
                        </a>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardBody>
          </Card>
          <Card
            className={cardClassName}
            style={getCardStyle(pastDueStats.pastDueColor)}
          >
            <CardHeader className="pt-1">
              <h4 className="mb-0">{t('accountsInfoCards.paymentInfo')}</h4>
            </CardHeader>
            <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
              <div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.actualPastDue')}:</span>
                  <span className="aic-value" style={{ color: pastDueStats.pastDueColor }}>
                    {pastDueStats.actualAmount} {pastDueStats.oldestDateText}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.actualPmntsPd')}:</span>
                  <span className="aic-value" style={{ color: pastDueStats.pastDueColor }}>
                    {pastDueStats.actualCount}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.filteredPastDue')}:</span>
                  <span className="aic-value" style={{ color: pastDueStats.pastDueColor }}>
                    {pastDueStats.filteredAmount} {pastDueStats.oldestDateText}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.filteredPmntsPd')}:</span>
                  <span className="aic-value" style={{ color: pastDueStats.pastDueColor }}>
                    {pastDueStats.filteredCount}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.downPmtDue')}:</span>
                  <span className="aic-value" style={{ color: pastDueStats.pastDueColor }}>
                    {downPmtDueTotal}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.lastPaidDate')}:</span>
                  <span className="aic-value" style={{ color: pastDueStats.pastDueColor }}>
                    {lastPaidDateText}
                  </span>
                </div>
                {(isTheme2MobileExpanded || showDealDetails) && (
                  <>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.lastPaidAmount')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {lastPaidAmountText}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.tier')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: pastDueStats.pastDueColor }}
                      >
                        {pastDueStats.tierText}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.mgmtOfficer')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      ></span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.mgmtQueue')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      ></span>
                    </div>
                  </>
                )}
                {!isTheme2MobileExpanded && (
                <div className="mb-1">
                  <a
                    href="#deal-details"
                    className="aic-link"
                    style={{ color: getActiveColorStyle().borderColor }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDealDetails(!showDealDetails);
                    }}
                  >
                    {showDealDetails
                      ? t("dealInfoCards.lessDetails")
                      : t("dealInfoCards.moreDetails")}
                  </a>
                </div>
                )}
              </div>
            </CardBody>
          </Card>
          <Card
            className={cardClassName}
            style={getCardStyle(getAccountStatusAccentColor(data?.act_status, data?.flags, data?.is_bankruptcy))}
          >
            <CardHeader className="pt-1">
              <h4 className="mb-0">{t('accountsInfoCards.accountInfo')}</h4>
            </CardHeader>
            <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
              <div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.acctStatus')}:</span>
                  <span
                    className="aic-value"
                    style={{
                      color: getAccountStatusColor(data?.act_status, {
                        flags: data?.flags,
                        isBankruptcy: Boolean(data?.is_bankruptcy),
                      }),
                    }}
                  >
                    {actStatusText}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.acctType')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {accountTypeText}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.acctNumber')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {data?.booking_id || "-"}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.loanNumber')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {data?.inventory?.stock_number || "-"}
                  </span>
                </div>
                {data?.flags && data.flags.length > 0 && (
                  <div className="aic-row">
                    <span className="aic-label">{t('accountsInfoCards.flags')}:</span>
                    <span
                      className="aic-value"
                      style={{ color: "red", fontWeight: "bold" }}
                    >
                      {data.flags.map((f) => f.name.toUpperCase()).join(", ")}
                    </span>
                  </div>
                )}
                {(isTheme2MobileExpanded || showAccountDetails) && (
                  <>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.lender')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {data?.inventory_vehicle?.body_style || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.portfolio')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {data?.portfolio?.code +
                          " - " +
                          data?.portfolio?.name || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.source')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {(data?.source?.channel_id ?? "") +
                          " - " +
                          (data?.source?.name ?? "")}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.serviceBranch')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {data?.servicing_branch?.name || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.bookkeepingCo')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {(data?.portfolio?.bookkeeping_company?.channel_id ??
                          "") +
                          " - " +
                          data?.portfolio?.bookkeeping_company?.name
                          ? (data?.portfolio?.bookkeeping_company?.channel_id ??
                            "") +
                          " - " +
                          (data?.portfolio?.bookkeeping_company?.name ?? "")
                          : "" || "-"}
                      </span>
                    </div>
                  </>
                )}
                {!isTheme2MobileExpanded && (
                <div className="mb-1">
                  <a
                    href="#account-details"
                    style={{
                      color: getActiveColorStyle().borderColor,
                      textDecoration: "none",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAccountDetails(!showAccountDetails);
                    }}
                  >
                    {showAccountDetails
                      ? t("dealInfoCards.lessDetails")
                      : t("dealInfoCards.moreDetails")}
                  </a>
                </div>
                )}
              </div>
            </CardBody>
          </Card>
          <Card
            className={cardClassName}
            style={getCardStyle(promiseAccentColor)}
          >
            <CardHeader className="pt-1">
              <h4 className="mb-0">{t('accountsInfoCards.promiseToPay')}</h4>
            </CardHeader>
            <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
              <div>
                <div className="aic-row">
                  <span className="aic-label" style={{ minWidth: "auto" }}>
                    {t('accountsInfoCards.last')}:
                  </span>
                  <span className="aic-value" style={{ color: (!data?.promise_payment || data?.promise_payment?.total === 0) ? getTextColor() : data?.promise_payment?.last_promise_status == "pending" ? "#f6ad55" : data?.promise_payment?.last_promise_status == "kept" ? "#28a745" : "#ff5c5c" }}>
                    {(!data?.promise_payment || data?.promise_payment?.total === 0) ? "-" : (
                      <>
                        {data?.promise_payment?.last_promise_status == "pending" ? "Open " : data?.promise_payment?.last_promise_status == "kept" ? "Kept " : "Broken "}
                        {data?.promise_payment?.last_promise_date
                          ? formatDateForDisplay(
                            data?.promise_payment?.last_promise_date
                          )
                          : "-"}{" "}
                        {t("accountsInfoCards.for")}{" "}
                        {formatPriceWithDollar(
                          data?.promise_payment?.last_promise_amount
                        )}
                      </>
                    )}
                  </span>
                </div>
                <div className="aic-row">
                  <span
                    className="aic-label"
                    style={{ color: getTextColor(), marginLeft: "0px" }}
                  >
                    <b>{data?.promise_payment?.pending}</b> Open / <b>{data?.promise_payment?.kept}</b> Kept / <b>{data?.promise_payment?.broken}</b> Broken / <b>{data?.promise_payment?.total}</b> Total
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="d-flex flex-column flex-lg-row" style={{ gap: 12 }}>
          <Card
            className={cardClassName}
            style={getCardStyle(NEUTRAL_ACCENT_BORDER)}
          >
            <CardHeader className="pt-1">
              <h4 className="mb-0">{t('accountsInfoCards.addressInfo')}</h4>
            </CardHeader>
            <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
              <div>
                {data?.account_type === "wholesale" ? (
                  <>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.add1')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.wholesaler?.address_1 || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.add2')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.wholesaler?.address_2 || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.cityStateZip')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.wholesaler?.city ? capitalizeWords(data?.wholesaler?.city) : "-"}{", "}
                        {getStateLabel(data?.wholesaler?.state)}{" "}
                        {data?.wholesaler?.zip || "-"}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.add1')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.customer?.current_address?.address_1 || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.add2')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.customer?.current_address?.address_2 || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.cityStateZip')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.customer?.current_address?.city ? capitalizeWords(data?.customer?.current_address?.city) : "-"}{", "}
                        {getStateLabel(data?.customer?.current_address?.state_name)}{" "}
                        {data?.customer?.current_address?.zip_code || "-"}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </CardBody>
          </Card>
          <Card
            className={cardClassName}
            style={getCardStyle(insuranceAccentColor)}
          >
            <CardHeader className="pt-1">
              <h4 className="mb-0">{t('accountsInfoCards.insuranceInfo')}</h4>
            </CardHeader>
            <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
              <div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.insStatus')}:</span>
                  <span
                    className="aic-value"
                    style={{ color: insuranceDisplay.color }}
                  >
                    {insuranceDisplay.translationKey
                      ? t(
                          `accountsInfoCards.${insuranceDisplay.translationKey}`,
                          insuranceDisplay.fallbackLabel
                        )
                      : insuranceDisplay.fallbackLabel || "-"}
                  </span>
                </div>

                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.insCompany')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {data?.insurance_history?.[0]?.insurance_company_name ||
                      "-"}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.policyNumber')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {data?.insurance_history?.[0]?.insurance_policy_number ||
                      "-"}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.insExpire')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {formatDateForDisplay(
                      data?.insurance_history?.[0]?.expire_date || "-"
                    )}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.collDeductible')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {formatPriceWithDollar(
                      data?.insurance_history?.[0]?.collision_deductible || "-"
                    )}
                  </span>
                </div>
                {(isTheme2MobileExpanded || showInsuranceDetails) && (
                  <>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.compDeductible')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {formatPriceWithDollar(
                          data?.insurance_history?.[0]
                            ?.comprehensive_deductible || "-"
                        )}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.agent')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {data?.insurance_history?.[0]?.insurance_agent_name ||
                          "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.agentPhone')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {data?.insurance_history?.[0]?.insurance_agent_phone ||
                          "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.agentFax')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {data?.insurance_history?.[0]?.insurance_agent_phone ||
                          "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.companyPhone')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {data?.insurance_history?.[0]
                          ?.insurance_company_phone || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.companyFax')}:</span>
                      <span
                        className="aic-value"
                        style={{ color: getTextColor() }}
                      >
                        {data?.insurance_history?.[0]?.insurance_company_fax ||
                          "-"}
                      </span>
                    </div>
                  </>
                )}
                {!isTheme2MobileExpanded && (
                <div className="mb-1">
                  <a
                    href="#deal-details"
                    className="aic-link"
                    style={{ color: getActiveColorStyle().borderColor }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowInsuranceDetails(!showInsuranceDetails);
                    }}
                  >
                    {showInsuranceDetails
                      ? t("dealInfoCards.lessDetails")
                      : t("dealInfoCards.moreDetails")}
                  </a>
                </div>
                )}
              </div>
            </CardBody>
          </Card>
          <Card
            className={cardClassName}
            style={getCardStyle(NEUTRAL_ACCENT_BORDER)}
          >
            <CardHeader className="pt-1">
              <h4 className="mb-0">{t('accountsInfoCards.loanInfo')}</h4>
            </CardHeader>
            <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
              <div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.primaryLoanPrin')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {formatPriceWithDollar(mergedAccountBalances?.current_principal) || "-"}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.sideNotePrin')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {formatPriceWithDollar(
                      data?.side_note_loan_balance ??
                        mergedAccountBalances?.current_side_note_prin
                    ) || "$0.00"}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.cpiLoanBal')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {formatPriceWithDollar(data?.cpi_loan_balance) || "-"}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.totalBalance')}:</span>
                  <span className="aic-value" style={{ color: "#1d8cf8", fontWeight: "bold" }}>
                    {formatPriceWithDollar(data?.total_balance) || "-"}
                  </span>
                </div>
                {(isTheme2MobileExpanded || showLoanDetails) && (
                  <>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.autoPaySettings')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {data?.auto_pay_settings || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.principalInterest')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {formatPriceWithDollar(data?.principal_interest) || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.nextDue')}:</span>
                      <span className="aic-value" style={{ color: getTextColor(), fontWeight: "bold" }}>
                        {nextDueText}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.sideNoteInterest')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {formatPriceWithDollar(data?.side_note_interest) || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.downPmtDue')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {downPmtDueTotal}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.salesTaxDue')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {formatPriceWithDollar(mergedAccountBalances?.current_state_sales_tax) || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.miscFees')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {formatPriceWithDollar(mergedAccountBalances?.current_misc_fees) || "-"}
                      </span>
                    </div>
                    <div className="aic-row">
                      <span className="aic-label">{t('accountsInfoCards.lateFees')}:</span>
                      <span className="aic-value" style={{ color: getTextColor() }}>
                        {formatPriceWithDollar(mergedAccountBalances?.current_late_fee) || "-"}
                      </span>
                    </div>
                  </>
                )}
                {!isTheme2MobileExpanded && (
                <div className="mb-1">
                  <a
                    href="#loan-details"
                    className="aic-link"
                    style={{ color: getActiveColorStyle().borderColor }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowLoanDetails(!showLoanDetails);
                    }}
                  >
                    {showLoanDetails
                      ? t("dealInfoCards.lessDetails")
                      : t("dealInfoCards.moreDetails")}
                  </a>
                </div>
                )}
              </div>
            </CardBody>
          </Card>
          <Card
            className={cardClassName}
            style={getCardStyle(NEUTRAL_ACCENT_BORDER)}
          >
            <CardHeader className="pt-1">
              <h4 className="mb-0">{t("dealInfoCards.vehicleDetail")}</h4>
            </CardHeader>
            <CardBody className={cardBodyClassName} style={{ paddingTop: 5 }}>
              <div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.description')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {`${data?.inventory?.inventory_vehicle?.year || ""} ${data?.inventory?.inventory_vehicle?.make || ""} ${data?.inventory?.inventory_vehicle?.model || ""}` || "-"}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.stockNumber')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {data?.inventory?.vin ? (
                      <a
                        href={`/inventory/${data.inventory.documentId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: getTextColor(), textDecoration: 'underline' }}
                      >
                        {data?.inventory?.stock_number || "-"}
                      </a>
                    ) : (
                      data?.inventory?.stock_number || "-"
                    )}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.vin')}:</span>
                  <span className="aic-value" style={{ color: getTextColor(), wordBreak: "break-all", display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {data?.inventory?.vin ? (
                      <>
                          {data.inventory.vin}
                        <i
                          className="fa fa-copy"
                          style={{ cursor: 'pointer', fontSize: '13px' }}
                          title="Copy VIN"
                          onClick={() => {
                            navigator.clipboard.writeText(data.inventory.vin);
                            showNotification('success', 'VIN copied to clipboard');
                          }}
                        />
                      </>
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className="aic-row">
                  <span className="aic-label">{t('accountsInfoCards.lastLocated')}:</span>
                  <span className="aic-value" style={{ color: getTextColor() }}>
                    {"-"}
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function - only re-render if data actually changes
    return (
      prevProps.data === nextProps.data &&
      prevProps.activeColor === nextProps.activeColor &&
      prevProps.vehicleAge === nextProps.vehicleAge &&
      prevProps.daysInInventory === nextProps.daysInInventory &&
      prevProps.isTheme2 === nextProps.isTheme2 &&
      prevProps.isLightTheme === nextProps.isLightTheme
    );
  }
);

AccountsInfoCards.displayName = "AccountsInfoCards";

export default AccountsInfoCards;
